/**
 * PublicLayout Component
 * Layout wrapper for public-facing pages with navigation
 */

import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState, type ReactNode } from 'react'
import { Moon, Sun, Menu, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import CurrencySelector from '../currency/CurrencySelector'
import { Logo } from '../ui'
import { PublicPageMeta } from '../seo/PublicPageMeta'
import { getPublicLayoutSeo } from '../../config/publicLayoutSeo'
import styles from './PublicLayout.module.css'

interface PublicLayoutProps {
  children: ReactNode
}

export const PublicLayout = ({ children }: PublicLayoutProps) => {
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navLinks = [
    { path: '/features', label: 'Features' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/faq', label: 'FAQ' },
    { path: '/documentation', label: 'Docs' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ]

  const footerColumns = [
    {
      title: 'Product',
      links: [
        { path: '/features', label: 'Features' },
        { path: '/pricing', label: 'Pricing' },
        { path: '/shopify-card-stores', label: 'For Shopify stores' },
        { path: '/in-store-sales', label: 'In-store sales' },
        { path: '/supported-games', label: 'Supported games' },
        { path: '/changelog', label: 'Changelog' },
      ],
    },
    {
      title: 'Help',
      links: [
        { path: '/documentation', label: 'Documentation' },
        { path: '/faq', label: 'FAQ' },
        { path: '/contact?topic=support', label: 'Support' },
        { path: '/contact', label: 'Contact us' },
      ],
    },
    {
      title: 'Company',
      links: [
        { path: '/about', label: 'About' },
        { path: '/tos', label: 'Terms of Service' },
        { path: '/privacy', label: 'Privacy Policy' },
      ],
    },
  ]

  const themeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode'
  const themeButton = (
    <button type="button" className={styles.themeToggle} onClick={toggleTheme} aria-label={themeLabel} title={themeLabel}>
      {isDark ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  )
  const accountLink = (
    <Link
      to={isAuthenticated ? '/dashboard' : '/login'}
      className={styles.signInLink}
      onClick={() => setMobileMenuOpen(false)}
    >
      {isAuthenticated ? 'Dashboard' : 'Sign In'}
    </Link>
  )

  const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/'

  const isActivePath = (path: string) => {
    const current = normalizePath(location.pathname)
    const target = normalizePath(path)
    if (target === '/') return current === '/'
    return current === target || current.startsWith(`${target}/`)
  }

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  const seo = getPublicLayoutSeo(location.pathname)

  return (
    <div className={styles.layout}>
      <PublicPageMeta
        title={seo.title}
        description={seo.description}
        ogImagePath={seo.ogImagePath}
      />
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>
      {/* Navigation Header */}
      <header className={styles.header}>
        <nav className={styles.nav}>
          <div className={styles.navContent}>
            {/* Logo */}
            <Link to="/" className={styles.logoLink} onClick={() => setMobileMenuOpen(false)} aria-label="TCGEasy home">
              <div className={styles.logoWrapper}>
                <Logo variant="full" size="sm" />
                <span className={styles.brandText}>TCGEasy</span>
              </div>
            </Link>

            {/* Navigation Links */}
            <div
              id="public-primary-nav"
              className={`${styles.navLinks} ${mobileMenuOpen ? styles.navLinksOpen : ''}`}
            >
              {navLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${styles.navLink} ${isActivePath(item.path) ? styles.navLinkActive : ''}`}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
              <div className={styles.menuExtras}>
                {accountLink}
                <div className={styles.menuControls}>
                  <CurrencySelector compact />
                  {themeButton}
                </div>
              </div>
            </div>

            <div className={styles.navControls}>
              <CurrencySelector compact />
              {themeButton}
              {accountLink}
            </div>

            {!isAuthenticated && (
              <Link to="/signup" className={styles.navCtaLink} onClick={() => setMobileMenuOpen(false)}>
                Start Free
              </Link>
            )}

            <button
              type="button"
              className={styles.mobileMenuToggle}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="public-primary-nav"
              onClick={() => setMobileMenuOpen((open: boolean) => !open)}
            >
              {mobileMenuOpen ? <X size={22} strokeWidth={1.75} aria-hidden /> : <Menu size={22} strokeWidth={1.75} aria-hidden />}
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main id="main-content" className={styles.main} tabIndex={-1}>
        {children}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <strong className={styles.footerBrandName}>TCGEasy</strong>
            <p className={styles.footerText}>Inventory, pricing, and Shopify sync for trading card stores. Based in Australia.</p>
            <p className={styles.footerText}>&copy; {new Date().getFullYear()} TCGEasy. All rights reserved.</p>
          </div>
          {footerColumns.map((column) => (
            <nav key={column.title} className={styles.footerColumn} aria-label={column.title}>
              <h2 className={styles.footerTitle}>{column.title}</h2>
              {column.links.map((item) => (
                <Link key={item.path} to={item.path} className={styles.footerLink}>
                  {item.label}
                </Link>
              ))}
            </nav>
          ))}
        </div>
      </footer>
    </div>
  )
}

export default PublicLayout
